import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import LmsMenu from './lmspages/LmsMenu';
import MyCourses from './lmspages/MyCourses';
import CourseCatalog from './lmspages/CourseCatalog';
import Settings from './lmspages/Settings';
import { BASE_URL } from '../config/constants';
import { generateToken, TOKEN_HEADER } from '../utils/tokenUtils';
import '../styles/ProgramPages.css';
import './lmspages/LmsMenu.css';
import './lmspages/LmsLayout.css';

function Lms() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [enrollments, setEnrollments] = useState([]);
  const [stats, setStats] = useState({
    activeCourses: 0,
    certificates: 0,
    learningTime: 0
  });
  const [loading, setLoading] = useState(true);

  const handleSectionChange = (section) => {
    setActiveSection(section);
    console.log('Active section:', section);
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const userId = localStorage.getItem('userId') || 1;

      // Fetch user enrollments
      const enrollmentResponse = await fetch(`${BASE_URL}/enrollments/user/${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          [TOKEN_HEADER]: generateToken(),
        }
      });

      if (enrollmentResponse.ok) {
        const result = await enrollmentResponse.json();
        if (result.success) {
          const enrollmentData = result.data || [];
          setEnrollments(enrollmentData);

          // Calculate stats
          const activeCourses = enrollmentData.filter(e => e.status === 'active' || e.status === 'in_progress').length;
          const certificates = enrollmentData.filter(e => e.status === 'completed').length;
          const totalMinutes = enrollmentData.reduce((sum, e) => sum + (e.time_spent_minutes || 0), 0);
          const learningHours = Math.round(totalMinutes / 60);

          setStats({
            activeCourses,
            certificates,
            learningTime: learningHours
          });
        }
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleContinueCourse = (courseId) => {
    navigate(`/player?courseId=${courseId}`);
  };

  return (
    <div className="lms-container">
      <SEO 
        title="Learning Management System | Lead with Tech"
        description="Access our comprehensive Learning Management System with courses, workshops, and training materials."
        keywords="lms, learning management system, online courses, training, education"
      />

      <LmsMenu activeSection={activeSection} onSectionChange={handleSectionChange} />

      <div className="lms-main">
        <div className="lms-content">
          {activeSection === 'courses' ? (
            <MyCourses />
          ) : activeSection === 'catalog' ? (
            <CourseCatalog />
          ) : activeSection === 'settings' ? (
            <Settings />
          ) : activeSection === 'dashboard' ? (
            <div className="dashboard-content">
              {loading ? (
                <div className="loading-dashboard">
                  <div className="spinner-large"></div>
                  <p>Loading dashboard...</p>
                </div>
              ) : (
                <>
                  {/* Welcome Section */}
                  <div className="welcome-card">
                    <div className="welcome-text">
                      <h2>Welcome back! 👋</h2>
                      <p>Continue your learning journey and achieve your goals</p>
                    </div>
                    <div className="welcome-stats">
                      <div className="stat-item">
                        <span className="stat-icon">📚</span>
                        <div className="stat-details">
                          <span className="stat-value">{stats.activeCourses}</span>
                          <span className="stat-label">Active Courses</span>
                        </div>
                      </div>
                      <div className="stat-item">
                        <span className="stat-icon">🎓</span>
                        <div className="stat-details">
                          <span className="stat-value">{stats.certificates}</span>
                          <span className="stat-label">Certificates</span>
                        </div>
                      </div>
                      <div className="stat-item">
                        <span className="stat-icon">⏱️</span>
                        <div className="stat-details">
                          <span className="stat-value">{stats.learningTime}h</span>
                          <span className="stat-label">Learning Time</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Continue Learning */}
                  <div className="continue-learning">
                    <h3>Continue Learning</h3>
                    {enrollments.length > 0 ? (
                      <div className="courses-grid">
                        {enrollments
                          .filter(e => e.status === 'active' || e.status === 'in_progress')
                          .slice(0, 6)
                          .map((enrollment) => (
                            <div 
                              key={enrollment.id} 
                              className="course-card-lms"
                              onClick={() => handleContinueCourse(enrollment.course_id)}
                              style={{ cursor: 'pointer' }}
                            >
                              <div className="course-thumbnail-lms">
                                {enrollment.thumbnail_url ? (
                                  <img src={enrollment.thumbnail_url} alt={enrollment.course_title} />
                                ) : (
                                  <div style={{ 
                                    width: '100%', 
                                    height: '100%', 
                                    background: 'linear-gradient(135deg, #b91c1c 0%, #dc2626 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: '48px'
                                  }}>📚</div>
                                )}
                                <div className="course-progress-overlay">
                                  <span className="progress-text">{enrollment.progress_percentage || 0}% Complete</span>
                                </div>
                              </div>
                              <div className="course-info">
                                <h4>{enrollment.course_title}</h4>
                                <p className="course-instructor">{enrollment.course_code}</p>
                                <div className="progress-bar">
                                  <div className="progress-fill" style={{ width: `${enrollment.progress_percentage || 0}%` }}></div>
                                </div>
                              </div>
                            </div>
                          ))
                        }
                      </div>
                    ) : (
                      <div className="no-courses-message">
                        <p>No active courses yet. Start learning by browsing the course catalog!</p>
                        <button className="view-courses-btn" onClick={() => handleSectionChange('catalog')}>
                          Browse Courses
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Completed Courses */}
                  {enrollments.filter(e => e.status === 'completed').length > 0 && (
                    <div className="completed-courses">
                      <h3>🎓 Completed Courses</h3>
                      <div className="courses-grid">
                        {enrollments
                          .filter(e => e.status === 'completed')
                          .slice(0, 3)
                          .map((enrollment) => (
                            <div key={enrollment.id} className="course-card-lms completed">
                              <div className="course-thumbnail-lms">
                                {enrollment.thumbnail_url ? (
                                  <img src={enrollment.thumbnail_url} alt={enrollment.course_title} />
                                ) : (
                                  <div style={{ 
                                    width: '100%', 
                                    height: '100%', 
                                    background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: '48px'
                                  }}>✓</div>
                                )}
                                <div className="completed-badge">Completed</div>
                              </div>
                              <div className="course-info">
                                <h4>{enrollment.course_title}</h4>
                                <p className="course-instructor">{enrollment.course_code}</p>
                                <p className="completed-date">
                                  Completed: {new Date(enrollment.completed_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="placeholder-section">
              <div className="placeholder-icon-large">
                {activeSection === 'catalog' && '🔍'}
                {activeSection === 'progress' && '📈'}
                {activeSection === 'certificates' && '🎓'}
                {activeSection === 'assignments' && '📝'}
                {activeSection === 'calendar' && '📅'}
                {activeSection === 'resources' && '📁'}
                {activeSection === 'settings' && '⚙️'}
              </div>
              <h2>{activeSection.charAt(0).toUpperCase() + activeSection.slice(1)} Section</h2>
              <p>This section is coming soon. Stay tuned for updates!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Lms;
